import { useState, useCallback } from "react";
import { TicketsService } from "@/services";
import { Ticket as TicketType, TicketResposta } from "@/types";
import { Usuario } from "@/types";
import toast from "react-hot-toast";
import { checkTicketAccess } from "@/utils/ticketPermissions";
import { convertQuillMentionsToBackendFormat, convertBackendMentionsToQuillFormat } from "@/utils/mentionQuillHelper";

interface UseTicketResponsesProps {
  ticket: TicketType | null;
  usuario: Usuario | null;
  ticketId: number | undefined;
  onResponseAdded?: (response: TicketResposta) => void;
  onResponseUpdated?: (responseId: number, content: string) => void;
  onResponseDeleted?: (responseId: number) => void;
}

export const useTicketResponses = ({
  ticket,
  usuario,
  ticketId,
  onResponseAdded,
  onResponseUpdated,
  onResponseDeleted,
}: UseTicketResponsesProps) => {
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<TicketResposta | null>(
    null
  );
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  const handleSubmitResponse = useCallback(
    async (content: string, anexos: File[]) => {
      if (!ticketId || !ticket) return;

      if (!content.trim()) {
        toast.error("Por favor, digite uma mensagem antes de enviar");
        return;
      }

      if (!checkTicketAccess(ticket, usuario)) {
        toast.error("Você não tem permissão para responder a este ticket");
        return;
      }

      try {
        setIsSubmitting(true);
        // Converter menções do formato Quill (spans HTML) para formato backend (@[id:14])
        const contentForBackend = convertQuillMentionsToBackendFormat(content);
        const response = await TicketsService.createTicketResponse(
          ticketId,
          contentForBackend,
          anexos
        );

        if (onResponseAdded) {
          onResponseAdded(response);
        }

        toast.success("Resposta adicionada com sucesso!");
        return response;
      } catch (error) {
        console.error("Erro ao adicionar resposta:", error);
        toast.error("Erro ao adicionar resposta");
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [ticket, usuario, ticketId, onResponseAdded]
  );

  const handleEditMessage = useCallback(
    (resposta: TicketResposta) => {
      if (!ticket) return;

      if (!checkTicketAccess(ticket, usuario)) {
        toast.error("Você não tem permissão para editar mensagens neste ticket");
        return;
      }

      if (resposta.usuario_id !== usuario?.id) {
        toast.error("Apenas o autor da mensagem pode editá-la");
        return;
      }

      setEditingMessageId(resposta.id);

      // Primeiro, mostrar imediatamente o conteúdo original no editor
      // para garantir que o campo não fique em branco
      setEditingContent(resposta.conteudo || "");

      // Em seguida, de forma assíncrona, converter menções do formato backend (@[id:14])
      // para o formato esperado pelo Quill (spans HTML). Se falhar, o conteúdo original permanece.
      (async () => {
        try {
          const contentForQuill = await convertBackendMentionsToQuillFormat(
            resposta.conteudo || ""
          );
          setEditingContent(contentForQuill || (resposta.conteudo || ""));
        } catch (error) {
          console.warn("Erro ao converter menções para edição:", error);
        }
      })();
    },
    [ticket, usuario]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingMessageId || !editingContent.trim() || !ticket) return;

    if (!checkTicketAccess(ticket, usuario)) {
      toast.error("Você não tem permissão para editar mensagens neste ticket");
      return;
    }

    const messageToEdit = ticket.respostas?.find(
      (resp) => resp.id === editingMessageId
    );
    if (!messageToEdit || messageToEdit.usuario_id !== usuario?.id) {
      toast.error("Apenas o autor da mensagem pode editá-la");
      return;
    }

    try {
      setIsEditing(true);
      // Converter menções do formato Quill (spans HTML) para formato backend (@[id:14])
      const contentForBackend = convertQuillMentionsToBackendFormat(editingContent);
      const response = await TicketsService.updateTicketResponse(
        editingMessageId,
        contentForBackend
      );

      if (response.success) {
        if (onResponseUpdated) {
          onResponseUpdated(editingMessageId, editingContent);
        }

        setEditingMessageId(null);
        setEditingContent("");
        toast.success("Mensagem editada com sucesso!");
      } else {
        toast.error("Erro ao editar mensagem");
      }
    } catch (error) {
      console.error("Erro ao editar mensagem:", error);
      toast.error("Erro ao editar mensagem");
    } finally {
      setIsEditing(false);
    }
  }, [
    editingMessageId,
    editingContent,
    ticket,
    usuario,
    onResponseUpdated,
  ]);

  const handleDeleteMessage = useCallback(
    (resposta: TicketResposta) => {
      if (!ticket) return;

      if (!checkTicketAccess(ticket, usuario)) {
        toast.error("Você não tem permissão para excluir mensagens neste ticket");
        return;
      }

      if (resposta.usuario_id !== usuario?.id) {
        toast.error("Apenas o autor da mensagem pode excluí-la");
        return;
      }

      setMessageToDelete(resposta);
    },
    [ticket, usuario]
  );

  const confirmDeleteMessage = useCallback(async () => {
    if (!messageToDelete || !ticket) return;

    if (!checkTicketAccess(ticket, usuario)) {
      toast.error("Você não tem permissão para excluir mensagens neste ticket");
      return;
    }

    if (messageToDelete.usuario_id !== usuario?.id) {
      toast.error("Apenas o autor da mensagem pode excluir mensagens neste ticket");
      return;
    }

    try {
      setIsDeletingMessage(true);
      const response = await TicketsService.deleteTicketResponse(
        messageToDelete.id
      );

      if (response.success) {
        if (onResponseDeleted) {
          onResponseDeleted(messageToDelete.id);
        }

        setMessageToDelete(null);
        toast.success("Mensagem excluída com sucesso!");
      } else {
        toast.error("Erro ao excluir mensagem");
      }
    } catch (error) {
      console.error("Erro ao excluir mensagem:", error);
      toast.error("Erro ao excluir mensagem");
    } finally {
      setIsDeletingMessage(false);
    }
  }, [messageToDelete, ticket, usuario, onResponseDeleted]);

  return {
    editingMessageId,
    editingContent,
    isEditing,
    isSubmitting,
    messageToDelete,
    isDeletingMessage,
    setEditingContent,
    handleSubmitResponse,
    handleEditMessage,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteMessage,
    confirmDeleteMessage,
    setMessageToDelete,
  };
};

